import React from "react";
import {
  TextField,
  SxProps,
  Theme,
  Box,
  Popper,
  Paper,
  Button,
  IconButton,
  InputAdornment,
  ClickAwayListener,
  MenuItem,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import "./TimePicker.css";

export interface TimePickerProps {
  value?: Date | null;
  onChange?: (value: Date | null) => void;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  fullWidth?: boolean;
  id?: string;
  externalError?: boolean;
  helperText?: string;
  placeholder?: string;
  suppressDefaultStyles?: boolean;
  minutesStep?: number;
  ampm?: boolean;
  inputFormat?: string;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const buildMinutes = (step: number) =>
  Array.from({ length: Math.ceil(60 / step) }, (_, i) => i * step).filter(
    (m) => m < 60,
  );

const pad2 = (n: number) => String(n).padStart(2, "0");

const formatTime = (date: Date | null | undefined): string => {
  if (!date || isNaN(date.getTime())) return "";
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

export const TimePicker: React.FC<TimePickerProps> = ({
  sx,
  fullWidth = true,
  onChange,
  value = null,
  id,
  externalError,
  helperText = "",
  placeholder = "ss:dd",
  suppressDefaultStyles = false,
  minutesStep = 5,
  disabled = false,
}) => {
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const hourListRef = React.useRef<HTMLDivElement>(null);
  const minuteListRef = React.useRef<HTMLDivElement>(null);

  const [open, setOpen] = React.useState(false);
  const [draftHour, setDraftHour] = React.useState<number | null>(null);
  const [draftMinute, setDraftMinute] = React.useState<number | null>(null);
  const draftHourRef = React.useRef<number | null>(null);
  const draftMinuteRef = React.useRef<number | null>(null);

  const updateDraftHour = (hour: number | null) => {
    draftHourRef.current = hour;
    setDraftHour(hour);
  };

  const updateDraftMinute = (minute: number | null) => {
    draftMinuteRef.current = minute;
    setDraftMinute(minute);
  };

  const minutes = React.useMemo(
    () => buildMinutes(Math.max(1, minutesStep)),
    [minutesStep],
  );

  const hasDraftSelection = draftHour !== null && draftMinute !== null;

  const displayValue = open
    ? hasDraftSelection
      ? `${pad2(draftHour!)}:${pad2(draftMinute!)}`
      : ""
    : formatTime(value);

  const scrollColumnToItem = (
    container: HTMLDivElement | null,
    item: HTMLElement | null,
  ) => {
    if (!container || !item) return;
    const top =
      item.offsetTop - container.clientHeight / 2 + item.clientHeight / 2;
    container.scrollTop = Math.max(0, top);
  };

  React.useEffect(() => {
    if (!open) return;

    const frame = requestAnimationFrame(() => {
      if (draftHour === null || draftMinute === null) {
        if (hourListRef.current) hourListRef.current.scrollTop = 0;
        if (minuteListRef.current) minuteListRef.current.scrollTop = 0;
        return;
      }

      const hourEl = hourListRef.current?.querySelector(
        `[data-hour="${draftHour}"]`,
      ) as HTMLElement | null;
      const minuteEl = minuteListRef.current?.querySelector(
        `[data-minute="${draftMinute}"]`,
      ) as HTMLElement | null;
      scrollColumnToItem(hourListRef.current, hourEl);
      scrollColumnToItem(minuteListRef.current, minuteEl);
    });

    return () => cancelAnimationFrame(frame);
  }, [open]);

  const openPicker = () => {
    if (disabled) return;

    if (value && !isNaN(value.getTime())) {
      let hour = value.getHours();
      let minute = value.getMinutes();

      if (!minutes.includes(minute)) {
        minute =
          minutes.reduce((prev, curr) =>
            Math.abs(curr - minute) < Math.abs(prev - minute) ? curr : prev,
          ) ?? 0;
      }

      updateDraftHour(hour);
      updateDraftMinute(minute);
    } else {
      updateDraftHour(null);
      updateDraftMinute(null);
    }

    setOpen(true);
  };

  const commitTime = (hour: number, minute: number) => {
    const next = value && !isNaN(value.getTime()) ? new Date(value) : new Date();
    next.setHours(hour, minute, 0, 0);
    onChange?.(next);
  };

  const handleClose = () => {
    const hour = draftHourRef.current;
    const minute = draftMinuteRef.current;
    if (hour !== null && minute !== null) {
      commitTime(hour, minute);
    }
    setOpen(false);
  };

  const handleSelectHour = (hour: number) => {
    const minute = draftMinuteRef.current === null ? 0 : draftMinuteRef.current;
    updateDraftHour(hour);
    updateDraftMinute(minute);
    commitTime(hour, minute);
  };

  const handleSelectMinute = (minute: number) => {
    const hour = draftHourRef.current === null ? 0 : draftHourRef.current;
    updateDraftMinute(minute);
    updateDraftHour(hour);
    commitTime(hour, minute);
  };

  const handleOk = () => {
    handleClose();
  };

  const rootClassName = [
    "time-picker",
    fullWidth ? "time-picker--full-width" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const inputClassName = suppressDefaultStyles ? undefined : "time-picker__input";

  return (
    <Box id={id} ref={anchorRef} className={rootClassName}>
      <TextField
        className={inputClassName}
        fullWidth={fullWidth}
        size="small"
        value={displayValue}
        placeholder={placeholder}
        disabled={disabled}
        error={!!externalError}
        helperText={helperText}
        onClick={openPicker}
        inputProps={{
          readOnly: true,
          placeholder,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (open) {
                    handleClose();
                  } else {
                    openPicker();
                  }
                }}
                edge="end"
                aria-label="Saat seç"
              >
                <AccessTimeIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={sx}
      />

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1400 }}
        modifiers={[
          { name: "offset", options: { offset: [0, 4] } },
          { name: "preventOverflow", options: { padding: 8 } },
        ]}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper elevation={8} className="time-picker__paper">
            <Box className="time-picker__columns">
              <Box
                ref={hourListRef}
                className="time-picker__column time-picker__column--hours"
              >
                {HOURS.map((hour) => (
                  <MenuItem
                    key={hour}
                    data-hour={hour}
                    selected={draftHour === hour}
                    onClick={() => handleSelectHour(hour)}
                    dense
                    className={[
                      "time-picker__item",
                      draftHour === hour ? "time-picker__item--selected" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {pad2(hour)}
                  </MenuItem>
                ))}
              </Box>
              <Box ref={minuteListRef} className="time-picker__column">
                {minutes.map((minute) => (
                  <MenuItem
                    key={minute}
                    data-minute={minute}
                    selected={draftMinute === minute}
                    onClick={() => handleSelectMinute(minute)}
                    dense
                    className={[
                      "time-picker__item",
                      draftMinute === minute
                        ? "time-picker__item--selected"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {pad2(minute)}
                  </MenuItem>
                ))}
              </Box>
            </Box>

            <Box className="time-picker__actions">
              <Button
                size="small"
                onClick={handleOk}
                className="time-picker__ok-btn"
              >
                Tamam
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

TimePicker.displayName = "TimePicker";
